import React, { useEffect, useState } from "react";
import { Plus, Edit2, Copy, Archive, X } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";

const EMPLOYMENT = ["Vollzeit", "Teilzeit", "Minijob", "Aushilfe", "Ausbildung"];
const AREAS = ["Küche", "Service/Verkauf", "Spülküche", "Leitung", "Verwaltung"];
const STATUS = [["draft", "Entwurf"], ["published", "Veröffentlicht"], ["paused", "Pausiert"], ["archived", "Archiviert"]];
const STATUS_STYLE = { draft: "bg-gray-100 text-gray-600", published: "bg-green-100 text-green-700", paused: "bg-amber-100 text-amber-700", archived: "bg-red-50 text-red-500" };

const EMPTY = { title: "", branch_id: "", employment_type: "Vollzeit", area: "Küche", short_description: "", intro: "", tasks: [], requirements: [], benefits: [], additional_info: "", start_date: "ab sofort", deadline: "", status: "draft", priority: 0 };

function ListField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-700">{label} <span className="text-gray-400 font-normal">(eine Zeile = ein Punkt)</span></label>
      <textarea rows={4} value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))}
        className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 focus:border-[#D62B3A] outline-none text-sm" data-testid={testid} />
    </div>
  );
}

export function CareersJobs() {
  const [jobs, setJobs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/careers/jobs").then((r) => setJobs(r.data));
  useEffect(() => { load(); api.get("/careers/meta").then((r) => setBranches(r.data.branches || [])); }, []);

  const save = async () => {
    if (!edit.title.trim() || !edit.branch_id) { toast.error("Titel und Standort sind Pflicht."); return; }
    try {
      if (edit.id) await api.put(`/admin/careers/jobs/${edit.id}`, edit);
      else await api.post("/admin/careers/jobs", edit);
      toast.success("Gespeichert");
      setEdit(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const duplicate = async (id) => { await api.post(`/admin/careers/jobs/${id}/duplicate`); toast.success("Dupliziert"); load(); };
  const archive = async (id) => { if (!window.confirm("Stelle archivieren?")) return; await api.delete(`/admin/careers/jobs/${id}`); toast.success("Archiviert"); load(); };

  const inp = "w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#D62B3A] outline-none text-sm";
  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-gray-500 text-sm">{jobs.length} Stellenanzeige(n)</p>
        <button onClick={() => setEdit({ ...EMPTY })} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#D62B3A] text-white font-semibold text-sm" data-testid="job-new-btn">
          <Plus className="w-4 h-4" /> Neue Stelle
        </button>
      </div>

      <div className="space-y-3">
        {jobs.map((j) => (
          <div key={j.id} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center justify-between gap-4" data-testid={`admin-job-${j.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold truncate">{j.title}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLE[j.status]}`}>{STATUS.find((s) => s[0] === j.status)?.[1]}</span>
              </div>
              <div className="text-sm text-gray-500 mt-0.5">{j.branch_name} · {j.employment_type} · {j.area} · Priorität {j.priority}</div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => setEdit(j)} className="p-2 rounded-lg hover:bg-gray-100" title="Bearbeiten" data-testid={`job-edit-${j.id}`}><Edit2 className="w-4 h-4" /></button>
              <button onClick={() => duplicate(j.id)} className="p-2 rounded-lg hover:bg-gray-100" title="Duplizieren" data-testid={`job-dup-${j.id}`}><Copy className="w-4 h-4" /></button>
              {j.status !== "archived" && <button onClick={() => archive(j.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Archivieren" data-testid={`job-archive-${j.id}`}><Archive className="w-4 h-4" /></button>}
            </div>
          </div>
        ))}
        {!jobs.length && <p className="text-gray-400">Noch keine Stellen. Lege die erste an.</p>}
      </div>

      {edit && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={() => setEdit(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8 p-6" onClick={(e) => e.stopPropagation()} data-testid="job-editor">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">{edit.id ? "Stelle bearbeiten" : "Neue Stelle"}</h3>
              <button onClick={() => setEdit(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <input placeholder="Jobtitel *" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className={inp} data-testid="job-f-title" />
              <div className="grid sm:grid-cols-2 gap-3">
                <select value={edit.branch_id} onChange={(e) => setEdit({ ...edit, branch_id: e.target.value })} className={inp} data-testid="job-f-branch">
                  <option value="">Standort wählen *</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <select value={edit.employment_type} onChange={(e) => setEdit({ ...edit, employment_type: e.target.value })} className={inp} data-testid="job-f-employment">
                  {EMPLOYMENT.map((t) => <option key={t}>{t}</option>)}
                </select>
                <select value={edit.area} onChange={(e) => setEdit({ ...edit, area: e.target.value })} className={inp} data-testid="job-f-area">
                  {AREAS.map((a) => <option key={a}>{a}</option>)}
                </select>
                <select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} className={inp} data-testid="job-f-status">
                  {STATUS.map((s) => <option key={s[0]} value={s[0]}>{s[1]}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Kurzbeschreibung (für Kachel, ~150 Zeichen)</label>
                <textarea rows={2} maxLength={200} value={edit.short_description} onChange={(e) => setEdit({ ...edit, short_description: e.target.value })} className={`${inp} mt-1`} data-testid="job-f-short" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Einleitung (Freitext)</label>
                <textarea rows={2} value={edit.intro} onChange={(e) => setEdit({ ...edit, intro: e.target.value })} className={`${inp} mt-1`} data-testid="job-f-intro" />
              </div>
              <ListField label="Aufgaben" value={edit.tasks} onChange={(v) => setEdit({ ...edit, tasks: v })} testid="job-f-tasks" />
              <ListField label="Anforderungen" value={edit.requirements} onChange={(v) => setEdit({ ...edit, requirements: v })} testid="job-f-requirements" />
              <ListField label="Wir bieten" value={edit.benefits} onChange={(v) => setEdit({ ...edit, benefits: v })} testid="job-f-benefits" />
              <div>
                <label className="text-sm font-medium text-gray-700">Zusätzliche Hinweise (optional)</label>
                <textarea rows={2} value={edit.additional_info} onChange={(e) => setEdit({ ...edit, additional_info: e.target.value })} className={`${inp} mt-1`} data-testid="job-f-additional" />
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><label className="text-xs text-gray-500">Eintrittsdatum</label><input value={edit.start_date} onChange={(e) => setEdit({ ...edit, start_date: e.target.value })} className={inp} data-testid="job-f-start" /></div>
                <div><label className="text-xs text-gray-500">Bewerbungsfrist</label><input type="date" value={edit.deadline || ""} onChange={(e) => setEdit({ ...edit, deadline: e.target.value })} className={inp} data-testid="job-f-deadline" /></div>
                <div><label className="text-xs text-gray-500">Priorität (höher = oben)</label><input type="number" value={edit.priority} onChange={(e) => setEdit({ ...edit, priority: parseInt(e.target.value || 0) })} className={inp} data-testid="job-f-priority" /></div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={save} className="flex-1 py-3 rounded-xl bg-[#D62B3A] text-white font-semibold" data-testid="job-save-btn">Speichern</button>
              <button onClick={() => setEdit(null)} className="px-6 py-3 rounded-xl border border-gray-200">Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
